import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ExternalLink,
  Youtube,
  Send,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
} from "lucide-react";
import GuestRequestModal from "@/components/landing/GuestRequestModal";
import {
  LightLeak,
  CornerWaveform,
} from "@/components/landing/AmbientMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PODCAST_LOGO =
  "https://images-pw.pixieset.com/site/2wOrp4/9lowbV/Gemini_Generated_Image_5h3iit5h3iit5h3i1-Recoveredcopy-4409adbf-1500.jpg";

const GRID = [
  "https://images-pw.pixieset.com/site/2wOrp4/K1GqWj/1760821273745-fe004b86-1500.jpg",
  "https://images-pw.pixieset.com/site/2wOrp4/lKbyR3/SBM00174-7077f7f8-1500.jpg",
  "https://customer-assets.emergentagent.com/job_cinematic-pods-1/artifacts/6jxtng3s_20250826_152022.jpg",
  "https://images-pw.pixieset.com/site/2wOrp4/jEOREE/IMG_9253copy-17854604-1500.jpg",
  "https://images-pw.pixieset.com/site/2wOrp4/wKK7yj/SBM02356-45b665e4-1500.jpg",
  "https://images-pw.pixieset.com/site/2wOrp4/yAZqRe/ytbanner3-ebe69401-1500.jpg",
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Spotlight = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      containScroll: "trimSnaps",
      dragFree: true, // momentum-inertia scrolling (no snap hiccup)
      duration: 40, // slower, smoother easing (default ~25)
    },
    [
      AutoScroll({
        speed: 0.6, // slow, cinematic drift
        startDelay: 1000,
        stopOnInteraction: false,
        stopOnMouseEnter: true, // pause on hover so titles stay readable
        stopOnFocusIn: true,
      }),
      WheelGesturesPlugin({
        forceWheelAxis: "x", // horizontal trackpad + wheel drives the carousel
      }),
    ],
  );
  const [igIndex, setIgIndex] = useState(0);

  // Fetch real YouTube uploads once on mount
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/youtube/videos?limit=12`)
      .then((r) => {
        if (mounted && Array.isArray(r.data?.videos) && r.data.videos.length) {
          setVideos(r.data.videos);
        } else if (mounted) {
          // fall back to placeholder tiles that deep-link to the channel
          setVideos(
            GRID.map((src, i) => ({
              id: `placeholder-${i}`,
              title: "",
              url: "https://www.youtube.com/@SB_Spotlight",
              thumbnail: src,
              published: "",
            })),
          );
        }
      })
      .catch(() => {
        if (mounted)
          setVideos(
            GRID.map((src, i) => ({
              id: `placeholder-${i}`,
              title: "",
              url: "https://www.youtube.com/@SB_Spotlight",
              thumbnail: src,
              published: "",
            })),
          );
      });
    return () => {
      mounted = false;
    };
  }, []);

  const igPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const igNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setIgIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section
      id="spotlight"
      data-testid="spotlight-section"
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-12 gap-6 items-end mb-14"
        >
          <motion.div variants={reveal} custom={0} className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#ff0055] mb-5">
              [ 04 · Spotlight ]
            </div>
            <h2
              data-testid="spotlight-heading"
              className="font-display font-black tracking-tighter text-[44px] md:text-[64px] lg:text-[80px] leading-[0.95]"
            >
              Small Business{" "}
              <span className="text-[#00f0ff] text-glow-cyan">Spotlight</span>{" "}
              <span className="italic font-light text-[#a395c3]">Podcast.</span>
            </h2>
          </motion.div>
          <motion.p
            variants={reveal}
            custom={1}
            className="col-span-12 md:col-span-4 text-[#a395c3] text-base md:text-[17px] leading-relaxed"
          >
            <span className="text-white font-semibold">
              Welcome to the Small Business Spotlight Podcast
            </span>{" "}
            — produced in the heart of Lakeland, FL. We sit down with Central
            Florida&apos;s founders, creators, and community leaders to unpack
            the real stories behind how they built (and keep building) their
            thing. Honest conversations, hard-won lessons, and a stage we
            built specifically to amplify the people moving our community
            forward. Your story could be next.
          </motion.p>
        </motion.div>

        {/* Featured video + side panel + carousel (mobile order: video → carousel → guest card) */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-12 gap-5"
        >
          <motion.div
            variants={reveal}
            custom={0}
            data-testid="spotlight-featured-video"
            className="col-span-12 lg:col-span-8 order-1"
          >
            {/* Mobile / tablet header — pill + waveform sit ABOVE the video so they never overlap YouTube controls */}
            <div className="lg:hidden mb-3 flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/10">
                <Youtube size={14} className="text-[#ff0055]" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-white">
                  Featured episode
                </span>
              </div>
              <div className="relative w-[92px] h-8">
                <CornerWaveform color="#22c55e" corner="tr" />
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden border border-white/10 group aspect-video bg-[#0a0612]">
              {/* Desktop-only overlays — hidden on mobile/tablet to keep the video clean */}
              <div className="hidden lg:inline-flex absolute top-5 left-5 z-10 items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 pointer-events-none">
                <Youtube size={14} className="text-[#ff0055]" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-white">
                  Featured episode
                </span>
              </div>
              <iframe
                src="https://www.youtube.com/embed/-RHTsrxTfsI?rel=0&modestbranding=1&color=white"
                title="More Than a Business: How LaDonica Whitfield is Building a Future for Her Child"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                className="absolute inset-0 w-full h-full"
                data-testid="spotlight-youtube-iframe"
              />
              {/* Corner accents on the featured video — desktop only */}
              <div className="hidden lg:block">
                <LightLeak color="#ff0055" corner="br" size={200} />
                <CornerWaveform color="#22c55e" corner="bl" />
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={reveal}
            custom={1}
            className="col-span-12 lg:col-span-4 order-3 lg:order-2 rounded-3xl p-7 md:p-9 bg-[#11091a]/70 backdrop-blur-md border border-white/10 relative overflow-hidden flex flex-col"
            data-testid="spotlight-guest-card"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#ff0055]/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-[#00f0ff]/10 blur-3xl pointer-events-none" />

            <div className="relative inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold border border-[#ff0055]/40 text-[#ff0055] bg-[#ff0055]/10">
              <span className="w-1 h-1 rounded-full bg-[#ff0055] animate-pulse" />
              Free · Our way of giving back to our community
            </div>

            <img
              src={PODCAST_LOGO}
              alt="Small Business Spotlight Logo"
              className="relative w-14 h-14 rounded-2xl object-cover border border-white/10 mt-5"
            />

            <div className="relative font-display font-black text-[28px] md:text-[34px] mt-5 leading-[1.05] tracking-tight">
              The light&apos;s on{" "}
              <span className="text-[#00f0ff] text-glow-cyan">you.</span>
            </div>

            <div className="relative mt-5 space-y-3.5 text-[#cfc4e0] text-[14px] leading-relaxed">
              <p>
                This is our way of{" "}
                <span className="text-white font-semibold">
                  giving back to the community.
                </span>
              </p>
              <p>
                We know what it&apos;s like to need exposure and not know where
                to begin. So we built{" "}
                <span className="text-white font-semibold">SB Spotlight</span> —
                a free platform where we shine the light on entrepreneurs like{" "}
                <span className="font-display italic text-[#ff0055] not-italic font-bold">
                  YOU.
                </span>
              </p>
            </div>

            <div className="relative mt-5 text-[12px] uppercase tracking-[0.28em] text-[#a395c3]">
              Want to be a guest?
            </div>

            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent("sb:open-guest"));
              }}
              data-testid="spotlight-guest-cta"
              className="mt-auto pt-5 group/cta relative z-10 cursor-pointer touch-manipulation w-full text-left"
            >
              <span className="inline-flex items-center justify-center gap-3 pl-5 pr-2 py-2.5 rounded-full bg-[#ff0055] text-white font-semibold text-sm group-hover/cta:bg-[#00f0ff] group-hover/cta:text-[#05050a] transition-colors w-full">
                Request your spotlight
                <span className="w-8 h-8 rounded-full bg-black/30 group-hover/cta:bg-[#05050a] flex items-center justify-center group-hover/cta:rotate-[-45deg] transition-all">
                  <Send size={13} className="text-white group-hover/cta:text-[#00f0ff] transition-colors" />
                </span>
              </span>
            </button>
          </motion.div>

        {/* YouTube channel showcase — moved inside the parent grid so it can sit between
            the featured video and the guest card on mobile (video → carousel → guest card). */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="col-span-12 order-2 lg:order-3 mt-4 lg:mt-8"
          data-testid="spotlight-youtube-carousel"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <Youtube size={18} className="text-[#ff0055]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#a395c3]">
                Latest on YouTube — @SB_Spotlight
              </span>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://www.youtube.com/@SB_Spotlight"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="spotlight-youtube-link"
                className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
              >
                Subscribe <ExternalLink size={12} />
              </a>
              <div className="hidden md:flex items-center gap-1.5">
                <button
                  onClick={igPrev}
                  aria-label="Previous"
                  data-testid="spotlight-yt-prev"
                  className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center hover:bg-white/[0.1]"
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  onClick={igNext}
                  aria-label="Next"
                  data-testid="spotlight-yt-next"
                  className="w-9 h-9 rounded-full bg-[#ff0055] text-white flex items-center justify-center hover:bg-[#00f0ff] hover:text-[#05050a] transition-colors"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-3 md:gap-4">
              {videos.map((v, i) => (
                <a
                  key={v.id + i}
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`spotlight-yt-slide-${i}`}
                  title={v.title || "Watch on YouTube"}
                  className="relative flex-[0_0_75%] sm:flex-[0_0_45%] md:flex-[0_0_32%] lg:flex-[0_0_24%] aspect-square overflow-hidden rounded-2xl border border-white/10 group block"
                >
                  <img
                    src={v.thumbnail}
                    alt={v.title || ""}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05050a]/85 via-[#05050a]/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-85 group-hover:opacity-100 transition-opacity">
                    <PlayCircle
                      size={56}
                      strokeWidth={1.25}
                      className="text-white drop-shadow-[0_4px_24px_rgba(255,0,85,0.55)] group-hover:scale-110 transition-transform"
                    />
                  </div>
                  <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Youtube size={14} className="text-white" />
                  </div>
                  {v.title && (
                    <div className="absolute left-3 right-3 bottom-3 text-[11px] leading-snug text-white font-medium line-clamp-2 drop-shadow-md">
                      {v.title}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-center gap-1.5" data-testid="spotlight-yt-dots">
            {videos.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  igIndex === i ? "w-7 bg-[#ff0055]" : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </motion.div>
        </motion.div>
      </div>
      <GuestRequestModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
};

export default Spotlight;
